/**
 * PayablesDueAlert
 * ----------------
 * The popup. When the desk opens the admin app, anything overdue or due inside
 * seven days surfaces once per session — because an invoice you cannot see is
 * an invoice you pay late.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AlertTriangle, Clock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const KEY = "sm_payables_due_seen";

export const PayablesDueAlert = ({ token }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    if (!token || sessionStorage.getItem(KEY)) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/invoice-audit/alerts/due`, { headers, params: { days: 7 } });
        if (cancelled || !r.data?.count) return;
        setData(r.data);
        setOpen(true);
        sessionStorage.setItem(KEY, "1");
      } catch { /* silent — never block the dashboard on an alert */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!open || !data) return null;

  const rows = [...(data.overdue || []).map((r) => ({ ...r, late: true })),
                ...(data.due_soon || [])].slice(0, 8);

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
      onClick={() => setOpen(false)}>
      <div className="bg-white rounded-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}
        data-testid="payables-due-alert">
        <div className="px-4 py-3 border-b border-slate-200 flex items-start justify-between">
          <div>
            <h3 className="text-sm font-black text-slate-900 inline-flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" /> Supplier invoices need paying
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {data.overdue.length} overdue ({money(data.overdue_total)}) ·
              {" "}{data.due_soon.length} due within {data.window_days} days ({money(data.due_soon_total)})
            </p>
          </div>
          <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"
            data-testid="due-alert-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-1.5 max-h-72 overflow-auto">
          {rows.map((r) => (
            <div key={r.id} className={`rounded-md border px-2.5 py-1.5 text-[11.5px] flex items-center gap-2 ${
              r.late ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`}>
              <span className="min-w-0">
                <strong>{r.supplier_name}</strong> · {r.invoice_number}
                {r.po_number ? ` · PO ${r.po_number}` : ""}
                {!r.approved && <span className="text-amber-800 font-bold"> · not approved</span>}
              </span>
              <span className={`ml-auto shrink-0 font-bold ${r.late ? "text-red-700" : "text-amber-800"}`}>
                {money(r.balance)} · {r.late ? `${r.days_past_due}d late` : `in ${r.days_to_due}d`}
              </span>
            </div>
          ))}
          {data.count > rows.length && (
            <p className="text-[11px] text-slate-500 inline-flex items-center gap-1 pt-1">
              <Clock className="w-3.5 h-3.5" /> and {data.count - rows.length} more
            </p>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-100 flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => setOpen(false)} data-testid="due-alert-later">
            Later
          </Button>
          <Button size="sm" className="bg-sky-700 hover:bg-sky-800" data-testid="due-alert-open"
            onClick={() => { setOpen(false); window.location.href = "/admin/backorders"; }}>
            Open the audit desk
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PayablesDueAlert;
